import type {
  CodeDiffSubject,
  CodePackageSubject,
  CodeReviewSubject,
  EvaluationSubject,
} from '@amzn/helianthus-arbiter-client';

/**
 * Only the length the help text quotes — the service owns the real limit
 * (SubjectDescription in the Lambda) and rejects anything longer.
 */
export const DESCRIPTION_MAX_LENGTH = 2000;

/** Declared to the API so evaluations raised here are distinguishable from automation callers. */
export const SOURCE = 'arbiter-web-ui';

export type SubjectType = 'codePackage' | 'codeReview' | 'codeDiff';

/** Which of the two mutually exclusive ways the caller supplies a diff. */
export type DiffSource = 'rawDiff' | 'diffURL';

/**
 * Form-state shape for one generated subject type: every member present and a string, because
 * these back controlled inputs.
 *
 * Deriving from the client rather than hand-listing fields is what keeps the form in step with
 * the model. A new member makes `emptyForm` fail to compile until someone decides whether to
 * surface it, instead of the page silently drifting as it did before.
 */
type FormFields<T> = { [K in keyof Required<T>]: string };

export type CodePackageState = FormFields<CodePackageSubject>;
export type CodeReviewState = FormFields<CodeReviewSubject>;
/** additionalContext carries pre-signed URLs supplied by automation, so it is not typed input. */
export type CodeDiffState = FormFields<Omit<CodeDiffSubject, 'additionalContext'>> & {
  diffSource: DiffSource;
};

export interface EvaluationFormState {
  subjectType: SubjectType;
  codePackage: CodePackageState;
  codeReview: CodeReviewState;
  codeDiff: CodeDiffState;
  description: string;
  bindleLock: string;
}

export type FieldName =
  | 'crId'
  | 'revisionId'
  | 'rawDiff'
  | 'diffURL'
  | 'diffPackageName'
  | 'packageName'
  | 'bindleLock';

export type FieldErrors = Partial<Record<FieldName, string>>;

/** A pristine form. Switching subject type resets to this, so no value leaks between subjects. */
export function emptyForm(subjectType: SubjectType = 'codePackage'): EvaluationFormState {
  return {
    subjectType,
    codePackage: { packageName: '', commitId: '', branchName: '', versionSet: '' },
    codeReview: { crId: '', revisionId: '' },
    codeDiff: {
      diffSource: 'rawDiff',
      rawDiff: '',
      diffURL: '',
      packageName: '',
      versionSet: '',
      branch: '',
      baseCommitId: '',
    },
    description: '',
    bindleLock: '',
  };
}

function validateSubject(form: EvaluationFormState, next: FieldErrors): void {
  if (form.subjectType === 'codePackage') {
    if (!form.codePackage.packageName.trim()) next.packageName = 'Package name is required';
    return;
  }

  if (form.subjectType === 'codeReview') {
    if (!form.codeReview.crId.trim()) next.crId = 'Code review ID is required';
    // Not a format check: a revision has to parse for buildSubject to send a number.
    const revision = form.codeReview.revisionId.trim();
    if (revision && !(/^[0-9]+$/.test(revision) && Number(revision) >= 1)) {
      next.revisionId = 'Revision must be a whole number of 1 or more';
    }
    return;
  }

  // Which of the two diff channels is required follows the chooser, not the model.
  if (form.codeDiff.diffSource === 'diffURL') {
    if (!form.codeDiff.diffURL.trim()) next.diffURL = 'Diff URL is required';
  } else if (!form.codeDiff.rawDiff.trim()) {
    next.rawDiff = 'Diff content is required';
  }
  if (!form.codeDiff.packageName.trim()) next.diffPackageName = 'Package name is required';
}

/**
 * Check only what cannot go stale: that required fields are filled, that the chosen diff channel
 * has a value, and that a revision parses as a number.
 *
 * Every format (CR id, commit SHA, bindle id, diff URL, description length) is deliberately NOT
 * checked here. The service enforces all of them and its message names the offending field, so a
 * second copy here could only drift — and when it drifted stricter it rejected input the API
 * would have accepted. Formats stay in each field's constraintText as guidance.
 *
 * @returns One message per offending field; an empty object means the form is submittable.
 */
export function validate(form: EvaluationFormState): FieldErrors {
  const next: FieldErrors = {};
  validateSubject(form, next);
  if (!form.bindleLock.trim()) next.bindleLock = 'Bindle lock is required';
  return next;
}

/** Build the typed union member the API expects. Optional fields are omitted rather than sent empty. */
export function buildSubject(form: EvaluationFormState): EvaluationSubject {
  if (form.subjectType === 'codePackage') {
    const { packageName, commitId, versionSet, branchName } = form.codePackage;
    return {
      codePackage: {
        packageName: packageName.trim(),
        commitId: commitId.trim() || undefined,
        versionSet: versionSet.trim() || undefined,
        branchName: branchName.trim() || undefined,
      },
    };
  }

  if (form.subjectType === 'codeReview') {
    const revision = form.codeReview.revisionId.trim();
    return {
      codeReview: {
        crId: form.codeReview.crId.trim(),
        revisionId: revision ? Number(revision) : undefined,
      },
    };
  }

  const { diffSource, rawDiff, diffURL, packageName, versionSet, branch, baseCommitId } = form.codeDiff;
  return {
    codeDiff: {
      rawDiff: diffSource === 'rawDiff' ? rawDiff.trim() : undefined,
      diffURL: diffSource === 'diffURL' ? diffURL.trim() : undefined,
      packageName: packageName.trim(),
      versionSet: versionSet.trim() || undefined,
      branch: branch.trim() || undefined,
      baseCommitId: baseCommitId.trim() || undefined,
    },
  };
}
